import dayjs from "dayjs";
import { t } from "i18next";
import moment from "moment";
import {
  HAP_TO_VND,
  LOYALTY_APPLY_SCOPE,
  LOYALTY_APPLY_SCOPE_LABEL,
  LOYALTY_PACKAGE_STATUS,
  LOYALTY_RECONCILIATION_DAYS,
  LOYALTY_STATUS_LABEL,
  LOYALTY_TIME_INTERVAL,
  LOYALTY_TIME_INTERVAL_LABEL,
  REWARD_UNIT,
  REWARD_UNIT_LABEL,
  REWARD_UNIT_OPTIONS,
} from "./constants";

/** Ngưỡng gợi ý Loại thời gian: < 31 ngày → Tháng, ngược lại → Quý */
export const TIME_INTERVAL_MONTH_MAX_DAYS = 31;

function rangeExclusiveEnd(start, end) {
  const result = [];
  for (let i = start; i < end; i += 1) result.push(i);
  return result;
}

/** Map Form/API dateRange → moment pair hợp lệ cho antd RangePicker */
export function toFormDateRangeMoment(value) {
  if (!value?.[0] || !value?.[1]) return null;

  const toMomentValue = (input) => {
    if (moment.isMoment(input)) return moment(input.valueOf());
    if (input?._isAMomentObject && input._d) return moment(input._d);
    return moment(input);
  };

  const start = toMomentValue(value[0]);
  const end = toMomentValue(value[1]);
  if (!start.isValid() || !end.isValid()) return null;
  return [start, end];
}

/** Panel mặc định RangePicker CT — mở đúng tháng của giá trị form (tránh rc-picker crash khi edit) */
export function getProgramDateRangePickerPanelValue(normalizedValue) {
  const fallback = moment().startOf("day");
  const start = normalizedValue?.[0] ? moment(normalizedValue[0].valueOf()) : fallback;
  const end = normalizedValue?.[1] ? moment(normalizedValue[1].valueOf()) : start.clone();
  return [start, end];
}

/** Chuẩn hóa moment — bỏ giây/ms lẻ từ BE (vd. 00:00:01 → 00:00:00) */
export function normalizeLoyaltyMoment(value) {
  return moment(value).seconds(0).milliseconds(0);
}

/**
 * Ngày kết thúc tối đa hợp lệ của gói (luôn lấy 23:59:59) sao cho KHÔNG vượt ngày kết thúc CT cha.
 * Gói chỉ chọn ngày → end mặc định 23:59:59; nếu CT cha kết thúc trước 23:59:59 trong ngày
 * thì ngày đó không trọn vẹn → phải lùi 1 ngày.
 * @returns {moment.Moment} moment tại 23:59:59 của ngày kết thúc hợp lệ
 */
export function getPackageMaxEndMoment(programEndTime) {
  const programEnd = moment(programEndTime).milliseconds(0);
  const sameDayEnd = programEnd.clone().endOf("day").milliseconds(0); // 23:59:59 cùng ngày
  if (sameDayEnd.isAfter(programEnd)) {
    return programEnd.clone().subtract(1, "day").endOf("day").milliseconds(0);
  }
  return sameDayEnd;
}

/**
 * Biên chọn ngày trên RangePicker gói: CT start … CT end (cho phép chọn ngày quá khứ).
 * Không có CT cha → không giới hạn (minDate/maxDate = null).
 */
export function getPackageDatePickerBounds(selectedProgram) {
  if (!selectedProgram?.startTime || !selectedProgram?.endTime) {
    return {
      minDate: null,
      maxDate: null,
      programStart: null,
      programEnd: null,
      effectiveStart: null,
    };
  }

  const programStart = normalizeLoyaltyMoment(selectedProgram.startTime);
  const programEnd = normalizeLoyaltyMoment(selectedProgram.endTime);
  const maxEnd = getPackageMaxEndMoment(selectedProgram.endTime); // lùi 1 ngày nếu CT cha kết thúc < 23:59:59

  return {
    minDate: programStart.clone().startOf("day"),
    maxDate: maxEnd.clone().startOf("day"),
    programStart,
    programEnd,
    effectiveStart: programStart,
  };
}

export function isPackageDateDisabled(current, selectedProgram) {
  if (!current) return false;
  const { minDate, maxDate } = getPackageDatePickerBounds(selectedProgram);
  if (minDate && current.isBefore(minDate, "day")) return true;
  if (maxDate && current.isAfter(maxDate, "day")) return true;
  return false;
}

/** Giá trị panel mặc định RangePicker — mở đúng tháng trong biên CT (tránh kẹt tháng hiện tại toàn disable) */
export function getPackageDateRangePickerPanelValue(bounds, normalizedValue) {
  const fallbackStart = bounds.minDate ? bounds.minDate.clone() : moment().startOf("day");
  let start = normalizedValue?.[0] ? moment(normalizedValue[0]) : fallbackStart;
  let end = normalizedValue?.[1] ? moment(normalizedValue[1]) : (bounds.maxDate?.clone() ?? start.clone());

  if (bounds.minDate && start.isBefore(bounds.minDate, "day")) {
    start = bounds.minDate.clone();
  }
  if (bounds.maxDate && end.isAfter(bounds.maxDate, "day")) {
    end = bounds.maxDate.clone();
  }
  if (end.isBefore(start, "day")) {
    end = start.clone();
  }

  return [start, end];
}

/**
 * Khoảng thời gian gói gợi ý từ CT cha — auto-fill đúng biên CT (đảm bảo nằm trong CT cha).
 * @returns {[moment.Moment, moment.Moment]|null}
 */
export function getEffectivePackageDateRangeFromProgram(selectedProgram) {
  if (!selectedProgram?.startTime || !selectedProgram?.endTime) return null;

  // Chỉ chọn ngày → auto-fill ép start về 00:00:00 và end về 23:59:59
  // Nếu CT cha kết thúc trước 23:59:59 → lùi 1 ngày để gói không vượt ngày cuối CT cha
  const programStart = moment(selectedProgram.startTime).startOf("day");
  const programEnd = getPackageMaxEndMoment(selectedProgram.endTime);

  if (!programStart.isBefore(programEnd)) return null;

  return [programStart.clone(), programEnd.clone()];
}

/** Độ dài dateRange (moment) tính theo ngày */
export function getDateRangeDurationInDays(dateRange) {
  if (!dateRange?.[0] || !dateRange?.[1]) return null;
  return dateRange[1].diff(dateRange[0], "days");
}

/** Gợi ý timeInterval từ độ dài khoảng thời gian (chỉ auto-fill, không giới hạn dropdown) */
export function suggestTimeIntervalFromDuration(durationInDays) {
  if (durationInDays == null) return null;
  return durationInDays < TIME_INTERVAL_MONTH_MAX_DAYS ? LOYALTY_TIME_INTERVAL.MONTH : LOYALTY_TIME_INTERVAL.QUARTER;
}

/** Gợi ý Loại thời gian từ CT cha — dùng full start/end CT, không dùng effective range */
export function suggestTimeIntervalFromProgram(selectedProgram) {
  if (!selectedProgram?.startTime || !selectedProgram?.endTime) return null;

  const duration = getDateRangeDurationInDays([
    normalizeLoyaltyMoment(selectedProgram.startTime),
    normalizeLoyaltyMoment(selectedProgram.endTime),
  ]);

  return suggestTimeIntervalFromDuration(duration);
}

/** disabledTime cho RangePicker — cho phép trùng chính xác start/end của chương trình cha */
export function buildPackageDateRangeDisabledTime(selectedProgram) {
  if (!selectedProgram) return undefined;

  const { programStart, programEnd, effectiveStart } = getPackageDatePickerBounds(selectedProgram);

  return (date, type) => {
    if (!date) return {};

    const panelDate = moment.isMoment(date) ? date : moment(date);
    if (!panelDate.isValid()) return {};

    // Chỉ giới hạn giờ bắt đầu khi ngày hiệu lực trùng ngày bắt đầu CT (CT chưa qua ngày start)
    if (type === "start" && panelDate.isSame(effectiveStart, "day") && effectiveStart.isSame(programStart, "day")) {
      return {
        disabledHours: () => rangeExclusiveEnd(0, programStart.hour()),
        disabledMinutes: (hour) => {
          if (hour < programStart.hour()) return rangeExclusiveEnd(0, 60);
          if (hour > programStart.hour()) return [];
          return rangeExclusiveEnd(0, programStart.minute());
        },
        disabledSeconds: (hour, minute) => {
          if (hour < programStart.hour() || minute < programStart.minute()) return rangeExclusiveEnd(0, 60);
          if (hour > programStart.hour() || minute > programStart.minute()) return [];
          return rangeExclusiveEnd(0, programStart.second());
        },
      };
    }

    if (type === "end" && panelDate.isSame(programEnd, "day")) {
      return {
        disabledHours: () => rangeExclusiveEnd(programEnd.hour() + 1, 24),
        disabledMinutes: (hour) => {
          if (hour > programEnd.hour()) return rangeExclusiveEnd(0, 60);
          if (hour < programEnd.hour()) return [];
          return rangeExclusiveEnd(programEnd.minute() + 1, 60);
        },
        disabledSeconds: (hour, minute) => {
          if (hour > programEnd.hour() || minute > programEnd.minute()) return rangeExclusiveEnd(0, 60);
          if (hour < programEnd.hour() || minute < programEnd.minute()) return [];
          return rangeExclusiveEnd(programEnd.second() + 1, 60);
        },
      };
    }

    return {};
  };
}

/**
 * Tính trạng thái chương trình / gói — dự phòng khi BE không trả sẵn `status`.
 * Dựa trên startTime, endTime, resultStatus. Có cửa sổ +10 ngày sau endTime để chờ đối soát
 * trước khi coi là đã hoàn tất.
 */
export function computeLoyaltyStatus(startTime, endTime, resultStatus = null) {
  const now = dayjs();
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  const reconciliationEnd = end.add(LOYALTY_RECONCILIATION_DAYS, "day");

  if (now.isBefore(start)) return LOYALTY_PACKAGE_STATUS.UPCOMING;
  if (!now.isAfter(end)) return LOYALTY_PACKAGE_STATUS.ONGOING;

  if (now.isAfter(reconciliationEnd)) {
    return LOYALTY_PACKAGE_STATUS.COMPLETED;
  }

  // end < now <= end + 10 ngày
  if (isReconciledResultStatus(resultStatus)) {
    return LOYALTY_PACKAGE_STATUS.RECONCILED;
  }

  return LOYALTY_PACKAGE_STATUS.RECONCILING;
}

function isReconciledResultStatus(resultStatus) {
  if (resultStatus == null || resultStatus === "") return false;
  return String(resultStatus).toUpperCase() === LOYALTY_PACKAGE_STATUS.RECONCILED;
}

/** Nhãn đơn vị thưởng hiển thị trên màn chi tiết (vd. Tiền/item) */
export function getRewardUnitDisplayLabel(unit) {
  const option = REWARD_UNIT_OPTIONS.find((o) => o.value === unit);
  return option?.label ?? REWARD_UNIT_LABEL[unit] ?? unit ?? "—";
}

/** Trả về nhãn tiếng Việt của trạng thái */
export function getLoyaltyStatusLabel(status) {
  return LOYALTY_STATUS_LABEL[status] ?? status;
}

/** Nhãn loại thời gian gói — Tháng / Quý / 6 tháng / Năm */
export function getLoyaltyTimeIntervalLabel(timeInterval) {
  return LOYALTY_TIME_INTERVAL_LABEL[timeInterval] ?? timeInterval ?? "—";
}

/**
 * Người tạo: ưu tiên createdBy.fullname; fallback lookup theo createdById khi BE chưa resolve User.
 */
export function getLoyaltyCreatedByDisplay(record, userNameById = {}) {
  if (record?.createdBy?.fullname) return record.createdBy.fullname;

  const id = record?.createdById ?? record?.createdBy?.id;
  if (id && userNameById[id]) return userNameById[id];

  return "—";
}

/**
 * Nhãn phạm vi áp dụng: Một sản phẩm / Nhiều sản phẩm.
 * Fallback theo numOfProducts khi API chưa trả applyScope.
 */
export function getLoyaltyApplyScopeLabel(applyScope, numOfProducts) {
  if (applyScope) return LOYALTY_APPLY_SCOPE_LABEL[applyScope] ?? applyScope;
  if (numOfProducts == null) return "—";
  return numOfProducts <= 1
    ? LOYALTY_APPLY_SCOPE_LABEL[LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT]
    : LOYALTY_APPLY_SCOPE_LABEL[LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS];
}

/** Fallback khi form/API chưa có applyScope (clone/edit); form tạo mới pre-fill MULTIPLE_PRODUCTS */
export function getEffectiveApplyScope(applyScope) {
  return applyScope ?? LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS;
}

/**
 * Format số tiền thưởng theo đơn vị:
 *   10000     → "10k"
 *   1500000   → "1.5M"
 *   1000000000 → "1B"
 */
export function formatRewardAmount(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const num = Number(amount);
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(num % 1_000_000_000 === 0 ? 0 : 1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1)}k`;
  return String(num);
}

/**
 * Số tiền hiển thị trên UI (chiết khấu/SP) và gửi BE awardValue.
 * Giá trị thưởng form = đúng giá trị mốc theo đơn vị (Tiền/item → chiết khấu/sp, không chia minQty).
 */
export function getRewardDisplayAmount(rewardAmount) {
  return Number(rewardAmount);
}

/** Alias — dùng khi map payload createPeriod */
export function mapRewardAmountToAwardValue(rewardAmount) {
  return getRewardDisplayAmount(rewardAmount);
}

/** Format hiển thị mốc trên cột Chiết khấu/SP */
export function formatMilestoneRewardDisplay(rewardAmount, rewardUnit) {
  return formatRewardDisplay(getRewardDisplayAmount(rewardAmount), rewardUnit);
}

/**
 * Format hiển thị thưởng đầy đủ: "10k/sp", "500k/mốc", "1M HAP"
 * Quà/gói: mỗi mốc đạt luôn tính là 1 bộ quà (đồng bộ với cách Mobile hiển thị/cộng dồn "Bộ quà"),
 * không phụ thuộc giá trị nhập ở "Giá trị thưởng".
 */
export function formatRewardDisplay(amount, unit) {
  if (unit === REWARD_UNIT.GIFT_PER_AWARD) return t("v2.loyaltyPackage.rewardDisplay.giftPerAward");

  const amountStr = formatRewardAmount(amount);
  const unitLabel = REWARD_UNIT_LABEL[unit] ?? unit;
  if (unit === REWARD_UNIT.HAP) return `${amountStr} ${unitLabel}`;
  return `${amountStr}/${unitLabel.replace("tiền/", "")}`;
}

/**
 * Tính maxQty của mốc thứ N dựa vào minQty của mốc N+1.
 * Quy tắc: maxQty(N) = minQty(N+1) - 1
 * Mốc cuối cùng không có maxQty (unlimited).
 *
 * @param {Array} milestones - mảng đã sắp xếp tăng dần theo minQty
 * @param {number} index - index của mốc cần tính
 * @returns {number|null} maxQty hoặc null nếu là mốc cuối
 */
export function calcMilestoneMaxQty(milestones, index) {
  if (index >= milestones.length - 1) return null;
  const nextMinQty = milestones[index + 1]?.minQty;
  if (nextMinQty == null) return null;
  return Number(nextMinQty) - 1;
}

/**
 * Validate danh sách mốc:
 * - Tối thiểu 1 mốc
 * - minQty(N+1) > maxQty(N) = minQty(N+1) - 1
 * @returns {{ valid: boolean, errorIndex: number|null }}
 */
export function validateMilestones(milestones) {
  if (!milestones || milestones.length < 1) {
    return {
      valid: false,
      errorIndex: null,
      message: t("v2.loyaltyPackage.validation.atLeastOneMilestoneRequired"),
    };
  }

  for (let i = 0; i < milestones.length - 1; i++) {
    const current = Number(milestones[i].minQty);
    const next = Number(milestones[i + 1].minQty);
    if (next <= current) {
      return {
        valid: false,
        errorIndex: i + 1,
        message: t("v2.loyaltyPackage.validation.milestoneMinQtyMustBeGreaterThan", { index: i + 2, prev: current }),
      };
    }
  }

  return { valid: true, errorIndex: null, message: null };
}

/**
 * Quy đổi "Giá trị thưởng" về VNĐ để so sánh giữa các mốc — chỉ HAP mới quy đổi (x HAP_TO_VND),
 * các đơn vị còn lại (Tiền/sp, Tiền/mốc, Quà/gói) so trực tiếp giá trị đã nhập, không nhân số lượng.
 */
export function normalizeRewardAmountToVnd(amount, unit) {
  const value = Number(amount);
  return unit === REWARD_UNIT.HAP ? value * HAP_TO_VND : value;
}

/**
 * Validate từng field của mỗi cột mốc để hiển thị warning inline ngay dưới field.
 * @returns {{ valid: boolean, errors: Array<{minQty?:string, rewardUnit?:string, rewardAmount?:string, displayPercent?:string, awardAmountTitle?:string}> }}
 */
export function validateMilestoneFields(milestones) {
  const list = milestones || [];
  const errors = list.map(() => ({}));

  const isEmpty = (val) => val === null || val === undefined || val === "";

  list.forEach((m, i) => {
    if (isEmpty(m.minQty)) {
      errors[i].minQty = t("v2.loyaltyPackage.validation.minQtyRequired");
    } else if (!(Number(m.minQty) > 0)) {
      errors[i].minQty = t("v2.loyaltyPackage.validation.minQtyMustBePositiveInt");
    }

    if (isEmpty(m.rewardUnit)) {
      errors[i].rewardUnit = t("v2.loyaltyPackage.validation.rewardUnitRequired");
    }

    if (isEmpty(m.rewardAmount)) {
      errors[i].rewardAmount = t("v2.loyaltyPackage.validation.rewardAmountRequired");
    } else if (!(Number(m.rewardAmount) > 0)) {
      errors[i].rewardAmount = t("v2.loyaltyPackage.validation.rewardAmountMustBePositive");
    }

    if (isEmpty(m.displayPercent)) {
      errors[i].displayPercent = t("v2.loyaltyPackage.validation.displayPercentRequired");
    } else if (Number(m.displayPercent) < 1 || Number(m.displayPercent) > 100) {
      errors[i].displayPercent = t("v2.loyaltyPackage.validation.displayPercentRange");
    }

    if (isEmpty(m.awardAmountTitle)) {
      errors[i].awardAmountTitle = t("v2.loyaltyPackage.validation.awardAmountTitleRequired");
    }
  });

  // Snapshot lỗi tự thân từng field (rỗng/không dương) trước khi vòng so sánh dưới đây ghi đè errors[i] —
  // dùng để quyết định "mốc trước có so sánh được không", tránh 1 mốc bị lỗi THỨ TỰ (so với mốc trước nó)
  // lại làm mốc NGAY SAU nó bị bỏ qua so sánh dây chuyền (mỗi cặp mốc liền kề phải được so độc lập).
  const ownFieldErrors = errors.map((e) => ({ ...e }));

  for (let i = 1; i < list.length; i++) {
    if (!errors[i].minQty && !ownFieldErrors[i - 1].minQty) {
      const prev = Number(list[i - 1].minQty);
      if (Number(list[i].minQty) <= prev) {
        errors[i].minQty = t("v2.loyaltyPackage.validation.minQtyMustBeGreaterThanPrevValue", { prev });
      }
    }
    if (!errors[i].displayPercent && !ownFieldErrors[i - 1].displayPercent) {
      const prevPercent = Number(list[i - 1].displayPercent);
      if (Number(list[i].displayPercent) <= prevPercent) {
        errors[i].displayPercent = t("v2.loyaltyPackage.validation.displayPercentMustBeGreaterThanPrevInline", {
          index: i,
        });
      }
    }
    if (!errors[i].rewardAmount && !ownFieldErrors[i - 1].rewardAmount) {
      const prev = normalizeRewardAmountToVnd(list[i - 1].rewardAmount, list[i - 1].rewardUnit);
      const curr = normalizeRewardAmountToVnd(list[i].rewardAmount, list[i].rewardUnit);
      if (curr <= prev) {
        errors[i].rewardAmount = t("v2.loyaltyPackage.validation.rewardAmountMustBeGreaterThanPrevValue");
      }
    }
  }

  const valid = errors.every((e) => Object.keys(e).length === 0);
  return { valid, errors };
}

/**
 * Validate "Giá trị thưởng tăng dần" cho cấu hình riêng theo sản phẩm (popup Cấu hình mốc chiết khấu).
 * Dùng giá trị hiệu lực (override nếu có, không thì ăn theo mốc cha) — minQty không liên quan tới rule này.
 * @returns {{ valid: boolean, errors: Record<string, string> }} errors keyed theo milestone.id
 */
export function validateMilestoneConfigRewardProgression(milestones, configs) {
  const effective = (milestones || []).map((m) => ({
    id: m.id,
    rewardAmount: configs?.[m.id]?.rewardAmount ?? m.rewardAmount,
    rewardUnit: configs?.[m.id]?.rewardUnit ?? m.rewardUnit,
  }));

  const errors = {};
  for (let i = 1; i < effective.length; i++) {
    const prev = normalizeRewardAmountToVnd(effective[i - 1].rewardAmount, effective[i - 1].rewardUnit);
    const curr = normalizeRewardAmountToVnd(effective[i].rewardAmount, effective[i].rewardUnit);
    if (curr <= prev) {
      errors[effective[i].id] = t("v2.loyaltyPackage.validation.rewardAmountMustBeGreaterThanPrevValue");
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Kiểm tra thời gian gói con nằm trong thời gian chương trình cha.
 * - Start CT từ BE có thể lẻ giây (:01) — normalize về :00 để auto-fill khớp.
 * - End CT, start/end gói: so sánh chính xác tới giây (chỉ bỏ ms).
 * @returns {boolean}
 */
export function isPeriodWithinProgram(periodStart, periodEnd, programStart, programEnd) {
  const dropMs = (v) => moment(v).milliseconds(0);

  const periodStartAt = dropMs(periodStart);
  const periodEndAt = dropMs(periodEnd);
  const programStartAt = normalizeLoyaltyMoment(programStart); // bỏ cả giây (lẻ từ BE)
  const programEndAt = dropMs(programEnd);

  if (!periodStartAt.isValid() || !periodEndAt.isValid()) return false;

  return !periodStartAt.isBefore(programStartAt) && !periodEndAt.isAfter(programEndAt);
}

/** Format datetime hiển thị: "26/02/2026 00:00:00" */
export function formatLoyaltyDatetime(value) {
  if (!value) return "—";
  return dayjs(value).format("DD/MM/YYYY HH:mm:ss");
}

/** Format date range: "26/02/2026 00:00:00 – 27/02/2026 00:00:00" */
export function formatLoyaltyDateRange(startTime, endTime) {
  if (!startTime && !endTime) return "—";
  return `${formatLoyaltyDatetime(startTime)} – ${formatLoyaltyDatetime(endTime)}`;
}

/** Format date range xuống 2 dòng (theo UI Figma chi tiết CT) */
export function formatLoyaltyDateRangeMultiline(startTime, endTime) {
  if (!startTime && !endTime) return "—";
  return {
    start: formatLoyaltyDatetime(startTime),
    end: formatLoyaltyDatetime(endTime),
  };
}

/** Format ngày: "01/08/2024" */
export function formatLoyaltyDate(value) {
  if (!value) return "—";
  return dayjs(value).format("DD/MM/YYYY");
}

/** Summary CT/LP: "Từ … Đến …" — cả range trống thì một "—" */
export function formatLoyaltyProgramSummaryDateRange(startTime, endTime, fromLabel, toLabel) {
  if (!startTime && !endTime) return "—";
  return `${fromLabel} ${formatLoyaltyDate(startTime)} ${toLabel} ${formatLoyaltyDate(endTime)}`;
}

// ── Program form helpers ──────────────────────────────────────────────────────

/** Map record API → giá trị Form (RangePicker dùng moment — antd 4) */
export function mapProgramToFormValues(record, { clearDates = false } = {}) {
  if (!record) return {};

  return {
    title: record.title,
    dateRange:
      !clearDates && record.startTime && record.endTime
        ? [moment(record.startTime), moment(record.endTime)]
        : undefined,
    termsUrl: record.termsUrl || record.policy || "",
  };
}

/** Map Form values → payload API */
export function buildProgramInput(formValues) {
  const [start, end] = formValues.dateRange || [];
  return {
    title: formValues.title?.trim(),
    startTime: start?.toISOString(),
    endTime: end?.toISOString(),
    termsUrl: formValues.termsUrl?.trim(),
  };
}

// ── Package form helpers ──────────────────────────────────────────────────────

let milestoneIdCounter = 0;

/** Sinh id tạm cho mốc mới trên form */
export function generateMilestoneId() {
  milestoneIdCounter += 1;
  return `ms-temp-${Date.now()}-${milestoneIdCounter}`;
}

/** Có mốc đã nhập liệu hoặc nhiều hơn 1 mốc (cần confirm khi đổi ngành hàng) */
export function hasConfiguredMilestones(milestones) {
  if (!milestones?.length) return false;
  if (milestones.length > 1) return true;
  return milestones.some((m) => m.minQty != null || m.rewardAmount != null || m.displayPercent != null);
}

/** Mốc mặc định khi khởi tạo form */
export function createDefaultMilestone() {
  return {
    id: generateMilestoneId(),
    minQty: undefined,
    rewardUnit: REWARD_UNIT.PER_PRODUCT,
    rewardAmount: undefined,
    displayPercent: undefined,
    awardAmountTitle: undefined,
  };
}

/** Format số tiền nhập liệu với dấu chấm phân cách hàng nghìn */
export function formatAmountInput(value) {
  if (value == null || value === "") return "";
  const num = Number(String(value).replace(/\./g, ""));
  if (Number.isNaN(num)) return "";
  return new Intl.NumberFormat("vi-VN").format(num);
}

/** Parse giá trị từ input đã format */
export function parseAmountInput(value) {
  if (value == null || value === "") return undefined;
  const num = Number(String(value).replace(/\./g, ""));
  return Number.isNaN(num) ? undefined : num;
}

/** Gắn maxQty computed vào từng mốc */
export function milestonesWithMaxQty(milestones) {
  return (milestones || []).map((m, index) => ({
    ...m,
    maxQty: calcMilestoneMaxQty(milestones, index),
  }));
}

/** Kiểm tra displayPercent tăng dần và trong khoảng 1–100 */
export function validateDisplayPercents(milestones) {
  for (let i = 0; i < (milestones || []).length; i++) {
    const pct = Number(milestones[i].displayPercent);
    if (pct < 1 || pct > 100) {
      return {
        valid: false,
        errorIndex: i,
        message: t("v2.loyaltyPackage.validation.displayPercentInvalid"),
      };
    }
    if (i > 0) {
      const prev = Number(milestones[i - 1].displayPercent);
      if (pct <= prev) {
        return {
          valid: false,
          errorIndex: i,
          message: t("v2.loyaltyPackage.validation.displayPercentMustBeGreaterThanPrev", { current: i + 1, prev: i }),
        };
      }
    }
  }
  return { valid: true, errorIndex: null, message: null };
}

/** Map period API → form values (clone/edit) */
export function mapPeriodToFormValues(period, { clearDates = false } = {}) {
  if (!period) return {};

  return {
    title: period.title,
    dateRange:
      !clearDates && period.startTime && period.endTime
        ? [moment(period.startTime), moment(period.endTime)]
        : undefined,
    categoryId: period.category?.id,
    loyaltyPackageId: period.loyaltyPackageId || period.loyaltyPackage?.id,
    timeInterval: period.timeInterval,
    applyScope: period.applyScope,
  };
}

/** Map period products API → state form */
export function mapPeriodProductsToForm(products, milestones) {
  return (products || []).map((pp) => {
    const configs = {};
    (pp.milestones || []).forEach((mc, index) => {
      const milestone = milestones?.find((m) => m.id === mc.milestoneId) ?? milestones?.[index];
      if (!milestone) return;

      // BE luôn trả config.awards đầy đủ cho mọi SP (kể cả khi chưa override riêng, giá trị
      // y hệt mốc cha) — nên chỉ coi là override thật khi giá trị khác mốc cha. Trùng giá trị
      // nghĩa là "chưa override" → không set config để tiếp tục ăn theo mốc cha khi user sửa
      // milestones (đúng hành vi màn Thêm mới).
      const isOverridden = mc.rewardAmount !== milestone.rewardAmount || mc.rewardUnit !== milestone.rewardUnit;
      if (isOverridden) {
        configs[milestone.id] = {
          rewardAmount: mc.rewardAmount,
          rewardUnit: mc.rewardUnit,
        };
      }
    });

    return {
      productId: pp.product?.id ?? pp.id,
      productName: pp.product?.name,
      productCode: pp.product?.code,
      milestoneConfigs: configs,
    };
  });
}

/** Kiểm tra sản phẩm đã cấu hình override riêng đủ mốc (modal Xem chi tiết) */
export function isProductMilestoneConfigured(product, milestones) {
  if (!milestones?.length) return false;
  return milestones.every(
    (m) => product.milestoneConfigs?.[m.id]?.rewardAmount != null && product.milestoneConfigs?.[m.id]?.rewardUnit
  );
}

/**
 * Tóm tắt chiết khấu theo mốc — ưu tiên override từng SP, fallback mốc chung (section B).
 * Tự cập nhật khi milestones prop đổi, không cần ghi vào product.milestoneConfigs.
 * @param {{ requireAllFilled?: boolean }} options — form: true (mặc định); detail: false để luôn hiện đủ mốc
 */
export function formatProductMilestoneSummary(product, milestones, options = {}) {
  const { requireAllFilled = true } = options;

  // BE GetPeriod: mỗi SP có config.awards riêng (có thể khác số mốc so với period.config.awards)
  const sourceMilestones = product.productAwards?.length > 0 ? product.productAwards : milestones;

  if (!sourceMilestones?.length) return [];

  const useProductAwards = product.productAwards?.length > 0;

  return sourceMilestones.reduce((lines, m, index) => {
    const cfg = useProductAwards ? null : product.milestoneConfigs?.[m.id];
    const amount = cfg?.rewardAmount ?? m.rewardAmount;
    const unit = cfg?.rewardUnit ?? m.rewardUnit;

    // Mốc nào chưa nhập đủ giá trị thưởng thì bỏ qua dòng đó — không chặn hiển thị các mốc đã điền xong
    if (requireAllFilled && (amount == null || !(Number(amount) > 0) || !unit)) return lines;

    const rewardDisplay = formatMilestoneRewardDisplay(amount, unit);
    lines.push(`Mốc ${index + 1}: ${rewardDisplay}`);
    return lines;
  }, []);
}

/** Chuỗi hóa payload form để so sánh thay đổi (edit — skip submit khi không đổi) */
export function serializePackageFormForCompare({ formValues, milestones, products }) {
  return JSON.stringify(buildPeriodInput({ formValues, milestones, products }));
}

/** Map form → payload CreateLoyaltyPackagePeriod */
export function buildPeriodInput({ formValues, milestones, products }) {
  const [start, end] = formValues.dateRange || [];
  const normalizedMilestones = milestonesWithMaxQty(milestones);

  return {
    title: formValues.title?.trim(),
    startTime: start?.toISOString(),
    endTime: end?.toISOString(),
    loyaltyPackageId: formValues.loyaltyPackageId,
    categoryId: formValues.categoryId,
    timeInterval: formValues.timeInterval,
    applyScope: formValues.applyScope,
    milestones: normalizedMilestones.map((m) => ({
      minQty: Number(m.minQty),
      maxQty: m.maxQty,
      rewardAmount: Number(m.rewardAmount),
      rewardUnit: m.rewardUnit,
      displayPercent: Number(m.displayPercent),
      awardAmountTitle: m.awardAmountTitle ?? undefined,
    })),
    products: (products || []).map((p) => ({
      productId: p.productId,
      milestones: normalizedMilestones.map((m) => {
        const override = p.milestoneConfigs?.[m.id];
        return {
          rewardAmount: Number(override?.rewardAmount ?? m.rewardAmount),
          rewardUnit: override?.rewardUnit ?? m.rewardUnit,
          awardAmountTitle: override?.awardAmountTitle ?? m.awardAmountTitle ?? undefined,
        };
      }),
    })),
  };
}

/**
 * Validate toàn form gói chiết khấu trước submit.
 * @returns {{ valid: boolean, message: string|null }}
 */
export function validatePackageForm({ formValues, milestones, products, selectedProgram }) {
  if (!formValues?.title?.trim()) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.titleRequired") };
  }

  const dateCheck = validatePackageDateRange(formValues.dateRange);
  if (!dateCheck.valid) return dateCheck;

  if (!formValues.categoryId) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.categoryRequired") };
  }
  if (!formValues.loyaltyPackageId) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.programRequired") };
  }
  if (!formValues.timeInterval) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.timeIntervalRequired") };
  }
  if (!formValues.applyScope) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.applyScopeRequired") };
  }

  if (selectedProgram) {
    const [start, end] = formValues.dateRange;
    if (!isPeriodWithinProgram(start, end, selectedProgram.startTime, selectedProgram.endTime)) {
      return {
        valid: false,
        message: t("v2.loyaltyPackage.validation.periodOutOfProgramRange"),
      };
    }
  }

  const msCheck = validateMilestones(milestones);
  if (!msCheck.valid) return { valid: false, message: msCheck.message };

  for (const m of milestones) {
    if (!m.minQty || Number(m.minQty) <= 0) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.minQtyMustBePositiveIntShort") };
    }
    if (!m.rewardAmount || Number(m.rewardAmount) <= 0) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.rewardAmountMustBePositive") };
    }
    if (!m.rewardUnit) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.rewardUnitRequired") };
    }
    if (m.displayPercent == null || Number(m.displayPercent) < 1 || Number(m.displayPercent) > 100) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.displayPercentInvalid") };
    }
    if (!m.awardAmountTitle || !m.awardAmountTitle.trim()) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.awardAmountTitleRequired") };
    }
  }

  const pctCheck = validateDisplayPercents(milestones);
  if (!pctCheck.valid) return { valid: false, message: pctCheck.message };

  const productCheck = validatePackageProducts({ applyScope: formValues.applyScope, products });
  if (!productCheck.valid) return productCheck;

  return { valid: true, message: null };
}

/**
 * Validate danh sách sản phẩm theo loại áp dụng — dùng hiển thị warning inline dưới bảng sản phẩm.
 * @returns {{ valid: boolean, message: string|null }}
 */
export function validatePackageProducts({ applyScope, products }) {
  const productCount = (products || []).length;

  if (applyScope === LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT) {
    if (productCount === 0) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.singleProductRequired") };
    }
    if (productCount > 1) {
      return { valid: false, message: t("v2.loyaltyPackage.validation.singleProductOnlyOne") };
    }
  }
  if (applyScope === LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS && productCount < 2) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.multipleProductsMinTwo") };
  }

  return { valid: true, message: null };
}

/** Validate date range gói: start >= today, end > start */
export function validatePackageDateRange(dateRange) {
  if (!dateRange?.[0] || !dateRange?.[1]) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.dateRangeRequired") };
  }
  const [start, end] = dateRange;
  // Cho phép ngày bắt đầu trong quá khứ — chỉ ràng buộc nằm trong CT cha (xử lý ở validatePackageForm).
  if (!end.isAfter(start)) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.endTimeMustBeAfterStartTime") };
  }
  if (end.isBefore(moment().startOf("day"), "day")) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.endTimeMustBeFromToday") };
  }
  return { valid: true, message: null };
}

/** Validate CT: cho phép start quá khứ, end > start, end >= hôm nay */
export function validateProgramDateRange(dateRange) {
  if (!dateRange?.[0] || !dateRange?.[1]) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.dateRangeRequired") };
  }
  const [start, end] = dateRange;
  // Cho phép ngày bắt đầu trong quá khứ.
  if (!end.isAfter(start)) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.endTimeMustBeAfterStartTime") };
  }
  if (end.isBefore(moment().startOf("day"), "day")) {
    return { valid: false, message: t("v2.loyaltyPackage.validation.endTimeMustBeFromToday") };
  }
  return { valid: true, message: null };
}

/** RangePicker CT — chặn ngày quá khứ */
export function isProgramDateDisabled(current) {
  if (!current) return false;
  return current.isBefore(moment().startOf("day"), "day");
}
